import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{type: 'success' | 'error' | 'info', text: string} | null>(null);
  const [isEmailSent, setIsEmailSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) { setMessage({ type: 'error', text: 'Please enter your email address.' }); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) { setMessage({ type: 'error', text: 'Please enter a valid email address.' }); return; }
    setIsLoading(true); setMessage(null);
    try {
      const redirectUrl = process.env.REACT_APP_SITE_URL ? `${process.env.REACT_APP_SITE_URL}/reset-password` : `${window.location.origin}/reset-password`;
      const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: redirectUrl });
      if (error) {
        setMessage({ type: 'error', text: error.message.includes('rate limit') ? 'Too many requests. Please try again later.' : 'An error occurred. Please try again.' });
      } else { setIsEmailSent(true); setMessage({ type: 'success', text: 'A password reset link has been sent to your email.' }); }
    } catch (error) { setMessage({ type: 'error', text: 'An error occurred while requesting a password reset.' }); }
    finally { setIsLoading(false); }
  };

  const handleResendEmail = async () => { await handleSubmit({ preventDefault: () => {} } as React.FormEvent); };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-[480px] xl:w-[540px] flex-shrink-0 bg-gradient-to-br from-forest-600 via-forest-800 to-forest-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute -top-20 -left-20 w-80 h-80 rounded-full border border-white/10" />
          <div className="absolute top-1/4 right-0 w-64 h-64 rounded-full border border-white/10" />
          <div className="absolute bottom-20 left-16 w-48 h-48 rounded-full bg-white/5" />
        </div>
        <div className="relative z-10 flex flex-col justify-between p-10 xl:p-14 w-full">
          <Link to="/" className="inline-flex items-baseline">
            <span className="font-display text-2xl font-bold tracking-[-0.04em] text-white">Libera</span><span className="font-display text-2xl font-bold tracking-[-0.04em] text-gold-400">Cure</span>
          </Link>
          <div>
            <h2 className="text-3xl xl:text-4xl font-bold text-white leading-tight mb-4">
              Don't Worry,<br />We've Got You
            </h2>
            <p className="text-forest-100 max-w-sm leading-relaxed">
              Enter your email and we'll send you a link to reset your password in seconds.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-forest-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <div>
              <p className="text-white text-sm font-medium">Secure Process</p>
              <p className="text-forest-200/70 text-xs">Reset link expires in 1 hour</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center px-6 sm:px-12 py-12 bg-cream-100">
        <div className="w-full max-w-[400px]">
          <div className="lg:hidden mb-10">
            <Link to="/" className="inline-flex items-baseline">
              <span className="font-display text-2xl font-bold tracking-[-0.04em] text-forest-800">Libera</span><span className="font-display text-2xl font-bold tracking-[-0.04em] text-gold-400">Cure</span>
            </Link>
          </div>

          {!isEmailSent ? (
            <>
              <div className="w-12 h-12 bg-forest-100 rounded-xl flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-gold-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
              </div>
              <h1 className="text-3xl font-display font-bold text-forest-900 mb-1 tracking-tight">Forgot password?</h1>
              <p className="text-warm-500 mb-8">No worries, we'll send you reset instructions.</p>

              {message && (
                <div className={`mb-6 p-3.5 rounded-lg text-sm flex items-start gap-2 ${
                  message.type === 'error' ? 'bg-red-50 text-red-700 border border-red-100' : 'bg-forest-50 text-forest-700 border border-forest-100'
                }`}>
                  <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {message.text}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-forest-700 mb-1.5">Email address</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={isLoading}
                    className="w-full px-4 py-3 bg-cream-50 border border-warm-300 rounded-xl text-sm focus:ring-2 focus:ring-forest-500/20 focus:border-forest-600 outline-none transition-all placeholder-warm-400"
                    placeholder="Enter your registered email" />
                </div>
                <button type="submit" disabled={isLoading}
                  className="w-full bg-forest-800 hover:bg-forest-700 disabled:bg-forest-300 text-cream-100 py-3 rounded-lg border border-forest-700/50 font-semibold text-sm transition-all cursor-pointer flex items-center justify-center shadow-sm">
                  {isLoading ? <><div className="w-4 h-4 border-2 border-cream-100 border-t-transparent rounded-full animate-spin mr-2" />Sending...</> : 'Send Reset Link'}
                </button>
              </form>
              <Link to="/login" className="mt-6 flex items-center justify-center gap-2 text-sm text-warm-500 hover:text-forest-700 transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                Back to login
              </Link>
            </>
          ) : (
            <>
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h1 className="text-3xl font-display font-bold text-forest-900 mb-1 tracking-tight">Check your email</h1>
              <p className="text-warm-500 mb-8">
                We sent a reset link to <span className="font-medium text-forest-700">{email}</span>
              </p>

              <div className="bg-cream-50 border border-warm-200 rounded-xl p-5 mb-6 space-y-3 shadow-card">
                {['Check your spam folder if you don\'t see it', 'The link is valid for 1 hour', 'Click the link to set a new password'].map((tip, i) => (
                  <div key={i} className="flex items-start gap-2.5 text-sm text-warm-600">
                    <svg className="w-4 h-4 text-gold-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {tip}
                  </div>
                ))}
              </div>

              <div className="space-y-3">
                <button onClick={handleResendEmail} disabled={isLoading}
                  className="w-full py-2.5 text-sm font-medium text-gold-400 border border-forest-200 rounded-lg hover:bg-forest-50 transition-all cursor-pointer disabled:opacity-50">
                  {isLoading ? 'Sending...' : 'Resend email'}
                </button>
                <Link to="/login" className="flex items-center justify-center gap-2 text-sm text-warm-500 hover:text-forest-700 transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                  Back to login
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
